import React, { useState, useEffect } from 'react';
import { X, Download, Loader2, AlertTriangle, Clock } from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';
import { useAit } from '../../../context/AitContext';
import toast from 'react-hot-toast';

export default function AitLeasingReport({ isOpen, onClose, propertyId, propertyName }) {
  const { theme } = useTheme();
  const { fetchLeasingReport } = useAit();
  const isDark = theme === 'dark';
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !propertyId) return;
    setLoading(true);
    fetchLeasingReport(propertyId).then(result => {
      if (result.success) setReport(result);
      else toast.error(result.error || 'Failed to load report');
      setLoading(false);
    });
  }, [isOpen, propertyId, fetchLeasingReport]);

  if (!isOpen) return null;

  const downloadCSV = () => {
    if (!report) return;
    const r = report;
    const rows = [
      ['Leasing Report', r.property_name],
      ['Date', r.report_date],
      [],
      ['OCCUPANCY'],
      ['Total Units', r.occupancy.total_units],
      ['Occupied', r.occupancy.occupied],
      ['Leased', r.occupancy.leased || 0],
      ['Notice', r.occupancy.notice || 0],
      ['Vacant', r.occupancy.vacant],
      ['Occupancy %', `${r.occupancy.occupancy_percentage}%`],
      [],
      ['SET-ASIDE BY BEDROOM'],
    ];
    Object.entries(r.set_aside_by_bedroom || {}).forEach(([br, data]) => {
      rows.push([br, `Total: ${data.total}`, `Occ: ${data.occupied}`, `Vac: ${data.vacant}`, ...Object.entries(data.by_designation || {}).map(([d, c]) => `${d}%: ${c}`)]);
    });
    rows.push([], ['DESIGNATION DISTRIBUTION']);
    Object.entries(r.designation_distribution || {}).forEach(([d, c]) => rows.push([`${d}% AMI`, c, 'units']));
    rows.push([], ['RENT LIMITS']);
    rows.push(['Designation', 'BR', 'Max Rent', 'Utility Allowance']);
    (r.rent_limits || []).forEach(rl => rows.push([`${rl.designation}%`, `${rl.bedroom_count} BR`, rl.max_rent, rl.utility_allowance || '']));
    rows.push([], ['VACANT UNITS']);
    rows.push(['Unit #', 'Building', 'BR', 'Sq Ft', 'Target Set-Aside', 'Rent', 'Days Vacant', 'ADA', 'Programs']);
    (r.vacant_units || []).forEach(v => {
      rows.push([v.unit_number, v.building || '', v.bedroom_count || '', v.square_footage || '',
        v.target_designation ? `${v.target_designation}%` : 'Undesignated',
        v.rent_amount || '', v.days_vacant ?? '', v.ada_accessible ? 'Yes' : '', (v.programs || []).join(', ')]);
    });
    rows.push([], ['LEASED — PENDING MOVE-IN']);
    rows.push(['Unit #', 'Building', 'BR', 'Designation', 'Tenant', 'Lease Start', 'Move-In']);
    (r.leased_pending || []).forEach(u => rows.push([u.unit_number, u.building || '', u.bedroom_count || '', u.current_designation ? `${u.current_designation}%` : '', u.tenant_name || '', u.lease_start_date || '', u.move_in_date || '']));
    rows.push([], ['NOTICE — UPCOMING VACANCIES']);
    rows.push(['Unit #', 'Building', 'BR', 'Designation', 'Tenant', 'Lease End', 'Move-Out']);
    (r.notice_upcoming || []).forEach(u => rows.push([u.unit_number, u.building || '', u.bedroom_count || '', u.current_designation ? `${u.current_designation}%` : '', u.tenant_name || '', u.lease_end_date || '', u.move_out_date || '']));
    rows.push([], ['LEASE EXPIRATIONS (next 90 days)']);
    rows.push(['Unit #', 'Building', 'Tenant', 'Designation', 'Lease End', 'Days Remaining']);
    (r.lease_expirations || []).forEach(le => rows.push([le.unit_number, le.building || '', le.tenant_name || '', le.current_designation ? `${le.current_designation}%` : '', le.lease_end_date, le.days_remaining]));
    rows.push([], ['RECERTIFICATION ALERTS']);
    rows.push(['Unit #', 'Building', 'Tenant', 'Designation', 'Recert Due', 'Days Remaining', 'Status']);
    (r.recert_alerts || []).forEach(ra => rows.push([ra.unit_number, ra.building || '', ra.tenant_name || '', ra.current_designation ? `${ra.current_designation}%` : '', ra.recert_due_date, ra.days_remaining, ra.status]));
    rows.push([], ['BUILDINGS']);
    rows.push(['BIN', 'Name', 'Total', 'Occupied', 'Leased', 'Notice', 'Vacant']);
    (r.buildings || []).forEach(b => rows.push([b.bin_number, b.name || '', b.total_units, b.occupied, b.leased || 0, b.notice || 0, b.vacant]));

    const csv = rows.map(row => row.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Leasing_Report_${r.property_name?.replace(/\s+/g, '_')}_${r.report_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const StatusBadge = ({ status, days }) => {
    const styles = {
      expired: 'bg-red-500/10 text-red-500',
      critical: 'bg-red-500/10 text-red-500',
      warning: 'bg-orange-500/10 text-orange-500',
      upcoming: 'bg-blue-500/10 text-blue-500',
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded font-medium ${styles[status] || styles.upcoming}`}>
        {days < 0 ? `${Math.abs(days)}d overdue` : `${days}d`}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className={`mx-4 w-full max-w-3xl max-h-[90vh] flex flex-col rounded-lg shadow-xl ${isDark ? 'bg-gray-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b shrink-0 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <h3 className="font-medium">Leasing Report — {propertyName}</h3>
          <div className="flex items-center gap-2">
            {report && (
              <button onClick={downloadCSV} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
                <Download size={12} /> Download CSV
              </button>
            )}
            <button onClick={onClose} className={`p-1 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 text-sm">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-blue-500" size={24} /></div>
          ) : !report ? (
            <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No data available</div>
          ) : (
            <div className="space-y-6">
              {/* Occupancy */}
              <Section title="Project Occupancy" isDark={isDark}>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                  {[
                    { label: 'Total Units', value: report.occupancy.total_units },
                    { label: 'Occupied', value: report.occupancy.occupied, color: 'text-green-500' },
                    { label: 'Leased', value: report.occupancy.leased || 0, color: 'text-blue-500' },
                    { label: 'Notice', value: report.occupancy.notice || 0, color: (report.occupancy.notice || 0) > 0 ? 'text-orange-500' : '' },
                    { label: 'Vacant', value: report.occupancy.vacant, color: report.occupancy.vacant > 0 ? 'text-orange-500' : '' },
                    { label: 'Occupancy', value: `${report.occupancy.occupancy_percentage}%` },
                  ].map(s => (
                    <div key={s.label} className={`p-3 rounded-lg ${isDark ? 'bg-gray-750' : 'bg-gray-50'}`}>
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{s.label}</p>
                      <p className={`text-lg font-semibold mt-0.5 ${s.color || ''}`}>{s.value}</p>
                    </div>
                  ))}
                </div>
              </Section>

              {/* Set-Aside by Bedroom */}
              {Object.keys(report.set_aside_by_bedroom || {}).length > 0 && (
                <Section title="Set-Aside by Unit Size" isDark={isDark}>
                  <Table isDark={isDark}
                    headers={['Unit Size', 'Total', 'Occ', 'Vac', 'Breakdown']}
                    rows={Object.entries(report.set_aside_by_bedroom).map(([br, data]) => [
                      br,
                      data.total,
                      <span className="text-green-500">{data.occupied}</span>,
                      <span className={data.vacant > 0 ? 'text-orange-500' : ''}>{data.vacant}</span>,
                      <div className="flex gap-1 flex-wrap">
                        {Object.entries(data.by_designation || {}).sort(([a], [b]) => a - b).map(([d, c]) => (
                          <span key={d} className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>{d}%:{c}</span>
                        ))}
                      </div>
                    ])}
                  />
                </Section>
              )}

              {/* Rent Limits */}
              {(report.rent_limits || []).length > 0 && (
                <Section title="Rent Limits Reference" isDark={isDark}>
                  <Table isDark={isDark}
                    headers={['Designation', 'Bedrooms', 'Max Rent', 'Utility Allowance']}
                    rows={report.rent_limits.map(rl => [
                      <span className="font-medium">{rl.designation}% AMI</span>,
                      `${rl.bedroom_count} BR`,
                      rl.max_rent ? `$${Number(rl.max_rent).toLocaleString()}` : '—',
                      rl.utility_allowance ? `$${Number(rl.utility_allowance).toLocaleString()}` : '—',
                    ])}
                  />
                </Section>
              )}

              {/* Vacant Units */}
              <Section title={`Vacant Units (${report.vacant_units?.length || 0})`} isDark={isDark}>
                {(report.vacant_units || []).length === 0 ? (
                  <p className={isDark ? 'text-gray-500' : 'text-gray-400'}>No vacant units — fully occupied</p>
                ) : (
                  <Table isDark={isDark}
                    headers={['Unit', 'Bldg', 'BR', 'Sq Ft', 'Target', 'Rent', 'Days Vacant']}
                    rows={report.vacant_units.map(v => [
                      v.unit_number,
                      v.building || '—',
                      v.bedroom_count || '—',
                      v.square_footage || '—',
                      v.target_designation
                        ? <span className="px-2 py-0.5 rounded bg-blue-500/10 text-blue-500 text-xs font-medium">{v.target_designation}%</span>
                        : <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Undesignated</span>,
                      v.rent_amount ? `$${Number(v.rent_amount).toLocaleString()}` : '—',
                      v.days_vacant != null
                        ? <span className={v.days_vacant > 30 ? 'text-red-500 font-medium' : v.days_vacant > 14 ? 'text-orange-500' : ''}>{v.days_vacant}d</span>
                        : '—',
                    ])}
                  />
                )}
              </Section>

              {/* Leased — Pending Move-In */}
              {(report.leased_pending || []).length > 0 && (
                <Section title={`Leased — Pending Move-In (${report.leased_pending.length})`} isDark={isDark}>
                  <Table isDark={isDark}
                    headers={['Unit', 'Bldg', 'BR', 'Designation', 'Tenant', 'Lease Start', 'Move-In']}
                    rows={report.leased_pending.map(u => [
                      u.unit_number,
                      u.building || '—',
                      u.bedroom_count || '—',
                      u.current_designation
                        ? <span className="px-2 py-0.5 rounded bg-blue-500/10 text-blue-500 text-xs font-medium">{u.current_designation}%</span>
                        : <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Undesignated</span>,
                      u.tenant_name || '—',
                      u.lease_start_date || '—',
                      u.move_in_date || '—',
                    ])}
                  />
                </Section>
              )}

              {/* Notice — Upcoming Vacancies */}
              {(report.notice_upcoming || []).length > 0 && (
                <Section title={`Notice — Upcoming Vacancies (${report.notice_upcoming.length})`} isDark={isDark} icon={<AlertTriangle size={14} className="text-orange-500" />}>
                  <Table isDark={isDark}
                    headers={['Unit', 'Bldg', 'BR', 'Designation', 'Tenant', 'Lease End', 'Move-Out']}
                    rows={report.notice_upcoming.map(u => [
                      u.unit_number,
                      u.building || '—',
                      u.bedroom_count || '—',
                      u.current_designation
                        ? <span className="px-2 py-0.5 rounded bg-blue-500/10 text-blue-500 text-xs font-medium">{u.current_designation}%</span>
                        : '—',
                      u.tenant_name || '—',
                      u.lease_end_date || '—',
                      u.move_out_date || '—',
                    ])}
                  />
                </Section>
              )}

              {/* Lease Expirations */}
              {(report.lease_expirations || []).length > 0 && (
                <Section title={`Lease Expirations — Next 90 Days (${report.lease_expirations.length})`} isDark={isDark} icon={<Clock size={14} className="text-orange-500" />}>
                  <Table isDark={isDark}
                    headers={['Unit', 'Bldg', 'Tenant', 'Designation', 'Lease End', 'Days']}
                    rows={report.lease_expirations.map(le => [
                      le.unit_number,
                      le.building || '—',
                      le.tenant_name || '—',
                      le.current_designation ? `${le.current_designation}%` : '—',
                      le.lease_end_date,
                      <StatusBadge status={le.days_remaining <= 30 ? 'critical' : le.days_remaining <= 60 ? 'warning' : 'upcoming'} days={le.days_remaining} />,
                    ])}
                  />
                </Section>
              )}

              {/* Recertification Alerts */}
              {(report.recert_alerts || []).length > 0 && (
                <Section title={`Recertification Alerts (${report.recert_alerts.length})`} isDark={isDark} icon={<AlertTriangle size={14} className="text-red-500" />}>
                  <Table isDark={isDark}
                    headers={['Unit', 'Bldg', 'Tenant', 'Designation', 'Recert Due', 'Status']}
                    rows={report.recert_alerts.map(ra => [
                      ra.unit_number,
                      ra.building || '—',
                      ra.tenant_name || '—',
                      ra.current_designation ? `${ra.current_designation}%` : '—',
                      ra.recert_due_date,
                      <StatusBadge status={ra.status} days={ra.days_remaining} />,
                    ])}
                  />
                </Section>
              )}

              {/* Buildings */}
              {(report.buildings || []).length > 1 && (
                <Section title="Buildings" isDark={isDark}>
                  <Table isDark={isDark}
                    headers={['BIN', 'Name', 'Total', 'Occupied', 'Leased', 'Notice', 'Vacant']}
                    rows={report.buildings.map(b => [
                      b.bin_number, b.name || '—', b.total_units, b.occupied, b.leased || 0, b.notice || 0, b.vacant,
                    ])}
                  />
                </Section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Reusable sub-components to keep the report clean
function Section({ title, isDark, icon, children }) {
  return (
    <div>
      <h4 className={`text-xs font-medium uppercase tracking-wider mb-3 flex items-center gap-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        {icon} {title}
      </h4>
      {children}
    </div>
  );
}

function Table({ isDark, headers, rows }) {
  return (
    <div className={`rounded-lg border overflow-x-auto ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
      <table className="w-full text-sm">
        <thead className={isDark ? 'bg-gray-750' : 'bg-gray-50'}>
          <tr>
            {headers.map((h, i) => (
              <th key={i} className={`text-left px-3 py-2 font-medium whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j} className="px-3 py-2 whitespace-nowrap">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
